import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import {
  useExamTypeMutations,
  useExamTypes,
} from '@/features/services/hooks';
import type { ExamTypeDTO } from '@/features/services/api';
import { examItemSchema, type ExamItemInput } from '@/lib/zodSchemas/service';

interface ExamTypesListProps {
  serviceId: string | null;
}

export function ExamTypesList({ serviceId }: ExamTypesListProps) {
  const { data: items = [], isLoading } = useExamTypes(serviceId);
  const mutations = useExamTypeMutations(serviceId);
  const toast = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [draft, setDraft] = useState<ExamItemInput>({ shortName: '', name: '', unitValue: 0 });
  const [draftErrors, setDraftErrors] = useState<Partial<Record<keyof ExamItemInput, string>>>({});

  if (!serviceId) {
    return (
      <p className="text-sm text-(--color-fg-muted)">
        Salve o serviço primeiro para adicionar exames.
      </p>
    );
  }

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    mutations.reorder.mutate({ id: String(active.id), newDisplayOrder: newIndex });
    // dica visual imediata já foi aplicada pelo optimistic do hook
    void arrayMove(items, oldIndex, newIndex);
  };

  const addItem = async () => {
    const parsed = examItemSchema.safeParse(draft);
    if (!parsed.success) {
      const errs: Partial<Record<keyof ExamItemInput, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof ExamItemInput | undefined;
        if (key) errs[key] = issue.message;
      }
      setDraftErrors(errs);
      return;
    }
    setDraftErrors({});
    try {
      await mutations.create.mutateAsync({
        shortName: parsed.data.shortName,
        name: parsed.data.name,
        value: parsed.data.unitValue,
      });
      setDraft({ shortName: '', name: '', unitValue: 0 });
    } catch (e) {
      toast.show({
        title: 'Não foi possível adicionar o exame',
        description: e instanceof ApiError ? e.message : 'Erro inesperado',
        variant: 'error',
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <fieldset className="rounded-md border border-(--color-border) p-3">
        <legend className="px-1 text-xs font-medium text-(--color-fg-muted)">Novo exame</legend>
        <div className="grid grid-cols-[1fr_2fr_1fr_auto] items-end gap-2">
          <FormField id="exam-new-short" label="Sigla" error={draftErrors.shortName}>
            <Input
              id="exam-new-short"
              value={draft.shortName}
              onChange={(e) => setDraft((d) => ({ ...d, shortName: e.target.value }))}
              placeholder="TC"
              invalid={!!draftErrors.shortName}
            />
          </FormField>
          <FormField id="exam-new-name" label="Nome" error={draftErrors.name}>
            <Input
              id="exam-new-name"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="Tomografia de abdômen"
              invalid={!!draftErrors.name}
            />
          </FormField>
          <FormField id="exam-new-value" label="Valor (R$)" error={draftErrors.unitValue}>
            <Input
              id="exam-new-value"
              value={String(draft.unitValue ?? '')}
              onChange={(e) =>
                setDraft((d) => ({ ...d, unitValue: e.target.value as unknown as number }))
              }
              inputMode="decimal"
              placeholder="120,00"
              invalid={!!draftErrors.unitValue}
            />
          </FormField>
          <Button type="button" onClick={addItem} disabled={mutations.create.isPending}>
            Adicionar
          </Button>
        </div>
      </fieldset>

      {isLoading ? (
        <p className="text-sm text-(--color-fg-muted)">Carregando exames…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-(--color-fg-muted)">
          Ainda não há exames cadastrados para este serviço.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <ul className="flex flex-col gap-1" aria-label="Lista ordenável de exames">
              {items.map((item) => (
                <SortableExamRow key={item.id} item={item} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function SortableExamRow({ item }: { item: ExamTypeDTO }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    shortName: item.shortName,
    name: item.name,
    unitValue: String(item.value),
  });
  const mutations = useExamTypeMutations(item.serviceId);
  const toast = useToast();

  const save = async () => {
    const parsed = examItemSchema.safeParse({
      shortName: draft.shortName,
      name: draft.name,
      unitValue: draft.unitValue,
    });
    if (!parsed.success) {
      toast.show({
        title: 'Campos inválidos',
        description: parsed.error.issues[0]?.message ?? 'Verifique os valores',
        variant: 'error',
      });
      return;
    }
    try {
      await mutations.update.mutateAsync({
        id: item.id,
        payload: {
          shortName: parsed.data.shortName,
          name: parsed.data.name,
          value: parsed.data.unitValue,
        },
      });
      setEditing(false);
    } catch (e) {
      toast.show({
        title: 'Erro ao salvar',
        description: e instanceof ApiError ? e.message : 'Tente novamente',
        variant: 'error',
      });
    }
  };

  const remove = async () => {
    if (!confirm(`Excluir "${item.name}"?`)) return;
    try {
      await mutations.remove.mutateAsync(item.id);
    } catch (e) {
      toast.show({
        title: 'Não foi possível excluir',
        description: e instanceof ApiError ? e.message : 'Tente novamente',
        variant: 'error',
      });
    }
  };

  if (editing) {
    return (
      <li
        ref={setNodeRef}
        style={style}
        className="grid grid-cols-[1fr_2fr_1fr_auto_auto] items-center gap-2 rounded-md border border-(--color-border) bg-(--color-surface-elevated) p-2"
      >
        <Input
          aria-label="Sigla"
          value={draft.shortName}
          onChange={(e) => setDraft((d) => ({ ...d, shortName: e.target.value }))}
        />
        <Input
          aria-label="Nome"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        />
        <Input
          aria-label="Valor"
          inputMode="decimal"
          value={draft.unitValue}
          onChange={(e) => setDraft((d) => ({ ...d, unitValue: e.target.value }))}
        />
        <Button size="sm" onClick={save} disabled={mutations.update.isPending}>
          Salvar
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          Cancelar
        </Button>
      </li>
    );
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="grid grid-cols-[auto_1fr_2fr_1fr_auto_auto] items-center gap-2 rounded-md border border-(--color-border) bg-(--color-surface-elevated) p-2"
    >
      <button
        type="button"
        aria-label={`Mover ${item.name}`}
        className="flex h-8 w-8 cursor-grab items-center justify-center rounded-sm text-(--color-fg-muted) hover:bg-(--color-surface) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent) active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="text-xs font-semibold text-(--color-fg-muted)">{item.shortName}</span>
      <span className="truncate text-sm text-(--color-fg)">{item.name}</span>
      <span className="text-sm tabular-nums text-(--color-fg-muted)">
        {item.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
      </span>
      <Button
        size="icon"
        variant="ghost"
        aria-label="Editar exame"
        onClick={() => setEditing(true)}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button size="icon" variant="ghost" aria-label="Excluir exame" onClick={remove}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}
